// Ian - 2026-08-13 - New PDP (Aug 2026 trial). Tab band on desktop, accordion on mobile.
//
// Deliberately a disclosure pattern (aria-expanded + aria-controls) rather than an
// ARIA tablist, even though it looks like tabs on desktop. The component changes
// presentation at 768px, and swapping role="tab"/"tabpanel" in and out on resize is
// where these components normally break for screen reader users. Disclosure semantics
// are correct and unchanging at both sizes, so nothing has to be re-announced.
//
// The theme's own <tabbed-content> was considered and not used: it sets the `hidden`
// attribute directly, which leaves no way for CSS to present the same panels as a
// stacked accordion on mobile.
//
// Panels are only ever hidden with CSS. They are never removed from the DOM and never
// lazy-injected, so the product description stays crawlable.
//
// The markup ships in its final state: one panel open, the rest closed, with aria-expanded
// matching. This script does not collapse anything on load, it only takes over the
// interaction.

if (!customElements.get('ian-pdp-tabs')) {
  class IanPdpTabs extends HTMLElement {
    connectedCallback() {
      this.triggers = Array.from(this.querySelectorAll('[data-ian-pdp-trigger]'));
      this.panels = Array.from(this.querySelectorAll('[data-ian-pdp-panel]'));
      this.tabButtons = Array.from(this.querySelectorAll('.ian-pdp__tab'));
      if (!this.triggers.length || !this.panels.length) return;

      // 767.98 rather than 768, to match the stylesheet's max-width: 767px exactly. At a
      // fractional width between the two (browser zoom, or a scrollbar-adjusted layout
      // width) the CSS showed the desktop tablist while this query still reported mobile,
      // so clicking the open tab collapsed it and left a tab row with no panel under it.
      this.desktop = window.matchMedia('(min-width: 767.98px)');

      // The markup already ships one panel open. This only re-asserts it, so that a panel
      // whose data-initial and data-open disagree cannot leave the band empty.
      const initial = this.panels.find((panel) => panel.dataset.initial === 'true') || this.panels[0];
      this.open(initial.id);

      this.addEventListener('click', this.handleClick.bind(this));
      this.addEventListener('keydown', this.handleKeydown.bind(this));

      this.onBreakpoint = this.handleBreakpoint.bind(this);
      if (this.desktop.addEventListener) {
        this.desktop.addEventListener('change', this.onBreakpoint);
      } else {
        this.desktop.addListener(this.onBreakpoint);
      }
    }

    disconnectedCallback() {
      if (!this.onBreakpoint) return;
      if (this.desktop.removeEventListener) {
        this.desktop.removeEventListener('change', this.onBreakpoint);
      } else {
        this.desktop.removeListener(this.onBreakpoint);
      }
    }

    handleClick(evt) {
      const trigger = evt.target.closest('[data-ian-pdp-trigger]');
      if (!trigger || !this.contains(trigger)) return;

      evt.preventDefault();
      const panelId = trigger.getAttribute('aria-controls');
      const isOpen = trigger.getAttribute('aria-expanded') === 'true';

      // On mobile an open panel can be collapsed again. On desktop it cannot, because
      // that would leave the band empty with no indication of why.
      if (isOpen && !this.desktop.matches) {
        this.closeAll();
        return;
      }
      if (isOpen) return;

      this.open(panelId);
    }

    // Left/right move between the desktop tabs. Not required for a disclosure group,
    // but the row reads as tabs, so it should behave like them under the keyboard.
    handleKeydown(evt) {
      if (evt.key !== 'ArrowLeft' && evt.key !== 'ArrowRight') return;

      const index = this.tabButtons.indexOf(document.activeElement);
      if (index === -1) return;

      evt.preventDefault();
      const step = evt.key === 'ArrowRight' ? 1 : -1;
      const next = (index + step + this.tabButtons.length) % this.tabButtons.length;
      this.tabButtons[next].focus();
    }

    handleBreakpoint() {
      // Coming back to desktop with everything collapsed would show an empty band.
      if (this.desktop.matches && !this.panels.some((panel) => panel.dataset.open === 'true')) {
        this.open(this.panels[0].id);
      }
    }

    open(panelId) {
      this.panels.forEach((panel) => {
        panel.dataset.open = String(panel.id === panelId);
      });
      this.triggers.forEach((trigger) => {
        trigger.setAttribute('aria-expanded', String(trigger.getAttribute('aria-controls') === panelId));
      });
    }

    closeAll() {
      this.panels.forEach((panel) => {
        panel.dataset.open = 'false';
      });
      this.triggers.forEach((trigger) => {
        trigger.setAttribute('aria-expanded', 'false');
      });
    }
  }

  customElements.define('ian-pdp-tabs', IanPdpTabs);
}
